import '../styles/globals.css';
import 'reflect-metadata';

import { Alert, CssBaseline, Snackbar, ThemeProvider } from '@mui/material';
import { AppCardService, MicrositeService, PackageService } from 'src/services';
import type { AppContext, AppProps } from 'next/app';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { CookiesProvider } from 'react-cookie';
import {
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import {
  SiteProvider,
  SnackbarProvider,
  UserProvider,
  useSnackbar,
} from 'src/store';

import App from 'next/app';
import appWithI18n from 'next-translate/appWithI18n';
import i18nConfig from '../../i18n';
import CookieBanner from 'src/components/CookieBanner/';
import Cookies from 'cookies';
import { Cookies as CookiesConsent } from 'react-cookie-consent';
import { IAppCardConfig } from '@orcha/micro-library-core-ui';
import { IMicrositeConfiguration } from 'src/interfaces/microsite';
import IPackageConfiguration from '@orcha/types/package';
import { IUserDetails } from 'src/interfaces/user';
import { NextPage } from 'next';
import ReactGA from 'react-ga4';
import { Services } from 'src/services/types';
import { Translate } from 'next-translate';
import decodeAccessToken from 'src/utilities/decodeAccessToken';
import { getDomainNameFromContext } from 'src/utilities/getDomainName';
import isValidDomain from 'is-valid-domain';
import { logPageviewAnalytic } from 'src/utilities/logAnalytics';
import { myContainer } from 'src/services/inversify.config';
import theme from 'src/themes';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { v4 } from 'uuid';
import useStorage from 'src/hooks/useStorage';
import createEmotionCache from '../utilities/createEmotionCache';
import Error from './_error';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (
    page: ReactElement,
    t: Translate,
    pageTitle?: string
  ) => ReactNode;
};

type MyAppProps = AppProps & {
  emotionCache?: EmotionCache;
  site?: IMicrositeConfiguration;
  packageConfig?: IPackageConfiguration;
  appCardConfig?: IAppCardConfig;
  Component: NextPageWithLayout;
  user?: IUserDetails;
};

const clientSideEmotionCache = createEmotionCache();

const GlobalSnackbar = () => {
  const { snackbarState, setSnackbarState } = useSnackbar();
  const { isOpen, autohideDuration, severity, content } = snackbarState;

  const handleSnackbarClose = (
    _event: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      isOpen: false,
    });
  };

  return (
    <Snackbar
      data-testid="global-snackbar"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={isOpen}
      autoHideDuration={autohideDuration ?? 2000}
      onClose={handleSnackbarClose}
    >
      <Alert severity={severity}>
        <div data-testid="global-snackbar-message">{content}</div>
      </Alert>
    </Snackbar>
  );
};

const MyApp = (props: MyAppProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { getItem, setItem } = useStorage();
  const [gaInitialized, setGaInitialized] = useState(false);

  const {
    Component,
    emotionCache = clientSideEmotionCache,
    pageProps,
    site,
    packageConfig,
    user,
    appCardConfig,
  } = props;

  const tracking = getItem('trackingId', 'local') as
    | { trackingId: string }
    | undefined;

  let { siteMode } = router.query ?? {};
  if (siteMode) {
    setItem('siteMode', { siteMode }, 'session');
  } else if (!siteMode) {
    const storedSiteMode = getItem('siteMode', 'session') as
      | { siteMode: string }
      | undefined;
    if (storedSiteMode && typeof storedSiteMode.siteMode === 'string') {
      siteMode = storedSiteMode.siteMode;
    } else {
      siteMode = '';
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && !gaInitialized) {
      const cookiesSet = CookiesConsent.get();
      ReactGA.initialize(process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? '', {
        testMode: process.env.NODE_ENV !== 'production',
        gtagOptions: {
          ad_storage:
            cookiesSet?.CookieConsent === 'true' ? 'granted' : 'denied',
          analytics_storage:
            cookiesSet?.CookieConsent === 'true' ? 'granted' : 'denied',
          functionality_storage:
            cookiesSet?.CookieConsent === 'true' ? 'granted' : 'denied',
          personalization_storage:
            cookiesSet?.CookieConsent === 'true' ? 'granted' : 'denied',
          security_storage:
            cookiesSet?.CookieConsent === 'true' ? 'granted' : 'denied',
          wait_for_update: 2000,
        },
      });
      setGaInitialized(true);
    }
  }, [gaInitialized]);

  useEffect(() => {
    const handleRouteChange = () => {
      logPageviewAnalytic({ page: window.location.href });
    };

    if (user) {
      setItem('user', user, 'session');
    }

    if (site) {
      setItem('site', site, 'session');
    }
    if (packageConfig) {
      setItem('packageConfig', packageConfig, 'session');
    }
    if (appCardConfig) {
      setItem('appCardConfig', appCardConfig, 'session');
    }

    if (!tracking?.trackingId) {
      setItem('trackingId', { trackingId: v4() }, 'local');
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  const { pageTitle } = (pageProps as any) ?? {};

  if (
    pageProps?.userHasRequiredPermissions !== 'undefined' &&
    pageProps.userHasRequiredPermissions === false
  ) {
    return <Error statusCode={403} />;
  }

  if (site) {
    return (
      <CookiesProvider>
        <SiteProvider
          siteData={site}
          packageConfig={packageConfig}
          appCardConfig={appCardConfig}
          siteMode={siteMode}
        >
          <UserProvider userDetails={user}>
            <CacheProvider value={emotionCache}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <SnackbarProvider>
                  {getLayout(
                    <>
                      <Component {...pageProps} />
                      <GlobalSnackbar />
                    </>,
                    t,
                    pageTitle
                  )}
                </SnackbarProvider>
                <CookieBanner />
              </ThemeProvider>
            </CacheProvider>
          </UserProvider>
        </SiteProvider>
      </CookiesProvider>
    );
  }

  return <Error statusCode={404} />;
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const { ctx } = appContext;

  const {
    ctx: { req, locale },
  } = appContext;

  const isServer = !!req;
  const isBrowser = !req;
  let site: IMicrositeConfiguration | undefined;
  let packageConfig: IPackageConfiguration | undefined;
  let user: IUserDetails | undefined;
  let appCardConfig: IAppCardConfig | undefined;

  if (isServer) {
    const fullDomainName = getDomainNameFromContext(ctx);

    if (isValidDomain(fullDomainName, { subdomain: true })) {
      const packageService = myContainer.get<PackageService>(
        Services.PackageService
      );

      packageConfig =
        await packageService.getPackageByDomainName(fullDomainName);
    }

    const cookies = new Cookies(req);
    const orchaAccessToken = cookies.get('orcha-access-token');

    user = decodeAccessToken(orchaAccessToken);
    if (user) {
      delete user.userId;
      // OTT-1988 (PT-07): roles and externalSource have no client-side reader,
      // so drop them at the source. Narrowing here rather than at the
      // sessionStorage write keeps them out of __NEXT_DATA__ too, and keeps the
      // client-nav shape identical to the server-render shape.
      delete user.roles;
      delete user.externalSource;
    }

    const micrositeService = myContainer.get<MicrositeService>(
      Services.MicrositeService
    );

    const appCardService = myContainer.get<AppCardService>(
      Services.AppCardService
    );

    if (packageConfig && !site) {
      const { tenantId, id, logoUrl } = packageConfig;
      site = await micrositeService.getMicrosite(tenantId, id, locale);
      if (site) {
        site.logoUrl = logoUrl;
      }
    }

    if (site && !appCardConfig && !!site.appCardConfigId) {
      const { tenantId, packageId, appCardConfigId } = site;
      appCardConfig = await appCardService.getAppCardConfig(
        tenantId,
        packageId,
        appCardConfigId
      );
    }
  }

  if (isBrowser) {
    // OTT-1988 (PT-07): sessionStorage is readable and editable by anyone with
    // the browser, and the `as` casts below are compile-time only. Shape-check
    // what comes back out so a malformed or hand-edited blob is discarded
    // rather than spread into the React tree. Discarding leaves the value
    // undefined — the same state as "storage was empty", which the `if (site)`
    // check above already handles. This is defence-in-depth; the real control
    // is server-side revalidation in the route guards.
    const isPlainObject = (value: unknown): value is Record<string, unknown> =>
      typeof value === 'object' && value !== null && !Array.isArray(value);

    const getItem = (key: string) => {
      const item = window.sessionStorage[key];
      if (item) {
        try {
          const data = JSON.parse(item);
          return isPlainObject(data) ? data : undefined;
        } catch (error) {
          console.error(error);
          return undefined;
        }
      }
    };

    // tenantId and packageId are required on IMicrositeConfiguration and are
    // always strings at runtime, so this rejects nothing legitimate.
    const getSite = (): IMicrositeConfiguration | undefined => {
      const data = getItem('site');
      if (
        !data ||
        typeof data.tenantId !== 'string' ||
        typeof data.packageId !== 'string'
      ) {
        return undefined;
      }
      return data as unknown as IMicrositeConfiguration;
    };

    user = getItem('user') as IUserDetails | undefined;
    site = getSite();
    packageConfig = getItem('packageConfig') as
      | IPackageConfiguration
      | undefined;
    appCardConfig = getItem('appCardConfig') as IAppCardConfig | undefined;
  }

  // Add config data to the request object so its available in each page's getServerSideProps function
  if (req) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (req as any).site = site;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ctx.req as any).packageConfig = packageConfig;
  }

  return {
    ...appProps,
    site,
    packageConfig,
    user,
    appCardConfig,
  };
};

// @ts-expect-error next-translate's appWithI18n typing doesn't account for custom AppProps
export default appWithI18n(MyApp, {
  ...i18nConfig,
  loader: false,
});
